/**
 * Client-side image compression - a field photo picked straight from a
 * device camera can easily exceed the evidence-upload size limit
 * (server/routers/approval.js's multer cap), and offline capture has no
 * chance to retry with a smaller file until the reporter is back online and
 * possibly out of the field. Compressing at capture time, before the file
 * ever reaches the offline queue or gets previewed, means what's queued and
 * eventually uploaded is already a reasonable size.
 *
 * Canvas-based (no new dependency) - draw the image scaled down to
 * maxDimension on its longest side, then re-encode as JPEG at the given
 * quality. Non-image files should never be passed in here; call sites are
 * expected to gate on kind === "image" themselves.
 */

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_QUALITY = 0.8;

export interface CompressImageOptions {
  maxDimension?: number;
  quality?: number;
}

/**
 * Returns a new, smaller File for an image, preserving its name. Falls back
 * to the original file untouched if compression fails for any reason (a
 * corrupt image, a browser without canvas support) - never blocks a capture
 * on a compression failure.
 */
export async function compressImageFile(file: File, options: CompressImageOptions = {}): Promise<File> {
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const quality = options.quality ?? DEFAULT_QUALITY;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;

    const compressedName = file.name.replace(/\.\w+$/, "") + ".jpg";
    return new File([blob], compressedName, { type: "image/jpeg", lastModified: file.lastModified });
  } catch {
    return file;
  }
}
